import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";

const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/706d1f5930a14eaf72024cc3c84e94282c656e2f638e2c6b1ffb9ef4ab172c5e.png";

const SERVICES = [
    {
        num: "01",
        kicker: "Core",
        title: "Business Development",
        body: "We open enterprise and strategic doors, structure partnerships, and negotiate revenue-generating agreements. Not intros. Not advisory. Closed business.",
        bullets: [
            "Enterprise partner identification and outreach",
            "Partnership and distribution deal structuring",
            "Contract negotiation and close",
        ],
    },
    {
        num: "02",
        kicker: "Channels",
        title: "Distribution Strategy + Execution",
        body: "Identify scalable revenue channels and activate multi-channel distribution. From zero to repeatable revenue.",
        bullets: [
            "CTV, OLV, social and commerce ecosystems",
            "Go-to-market strategy and channel activation",
            "Performance loops that convert",
        ],
    },
    {
        num: "03",
        kicker: "Pricing",
        title: "Monetization Frameworks",
        body: "We turn usage into actual dollars. Pricing, packaging, sponsorship and performance-based revenue models.",
        bullets: [
            "Pricing and packaging strategy",
            "Sponsorship and media deal structuring",
            "Performance-based revenue models",
        ],
    },
    {
        num: "04",
        kicker: "Operator layer",
        title: "Operator-Level Support",
        body: "Act as interim Head of BD / CRO layer — senior execution without adding payroll. Build repeatable systems for scale.",
        bullets: [
            "Interim Head of BD or CRO leadership",
            "Fundraising narrative — traction, pipeline, revenue clarity",
            "Repeatable BD systems and playbooks",
        ],
    },
];

const Services = () => {
    return (
        <main data-testid="page-services" className="text-white">
            <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-28 overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-[0.1]"
                    style={{ backgroundImage: `url(${TEXTURE})` }}
                    aria-hidden="true"
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-8">Services</Overline>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.98] max-w-5xl">
                            Four practices. <span className="italic text-accent">Built to move together.</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <p className="mt-10 max-w-2xl text-[15px] lg:text-lg leading-relaxed text-white/65">
                            Four execution levers, deployed selectively. Every engagement is wired to a single outcome:
                            closed deals and durable revenue.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="relative pb-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 divide-y divide-white/[0.06] border-y border-white/[0.06]">
                    {SERVICES.map((s, i) => (
                        <FadeIn key={s.num} delay={i * 0.05}>
                            <article
                                data-testid={`services-row-${s.num}`}
                                className="group grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 py-14 lg:py-20"
                            >
                                <div className="md:col-span-2">
                                    <span className="font-display text-5xl lg:text-6xl text-white/25 leading-none">{s.num}</span>
                                    <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/40">{s.kicker}</p>
                                </div>
                                <div className="md:col-span-6">
                                    <h2 className="font-display text-4xl lg:text-[3.25rem] leading-[1.05] text-white">{s.title}</h2>
                                    <p className="mt-6 text-[15px] lg:text-lg leading-relaxed text-white/65 max-w-xl">{s.body}</p>
                                </div>
                                <ul className="md:col-span-4 space-y-4 md:pt-3">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex gap-4 text-[14px] text-white/70 leading-relaxed">
                                            <span className="font-display text-white/35">—</span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        </FadeIn>
                    ))}
                </div>
            </section>

            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
                    <div className="md:col-span-8">
                        <FadeIn>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] text-white">
                                Curious where Orion Road fits in <span className="italic text-white/75">your stack?</span>
                            </h2>
                        </FadeIn>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact"
                            className="inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                        >
                            Book a conversation <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Services;
